import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, map, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { GenerateResponse, GenerateSettings } from '../models/visualizer.models';

export interface QuoteRequestProduct {
  surface: string;
  productCode: string;
  productName: string;
  pattern: string;
  boxQty: number;
  area: number | null;
}

export interface QuoteRequestPayload {
  customerEmail?: string;
  customerWhatsapp?: string;
  roomName: string;
  roomType: string;
  areaCount: number;
  selectedProducts: QuoteRequestProduct[];
  note: string;
}

export interface QuoteRequestResponse {
  success: boolean;
  message: string;
  whatsappUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VisualizerApiService {
  private readonly backendUrl = this.getBackendUrl();
  private baseUrl = (environment.visualizerApiBaseUrl || 'http://127.0.0.1:8000').replace(/\/+$/, '');

  private readonly templateRoomCache = new Map<string, Observable<any>>();
  private readonly assetFileCache = new Map<string, Observable<File>>();

  constructor(private http: HttpClient) {}

  getBaseUrl(): string {
    return this.baseUrl;
  }

  setBaseUrl(url: string): void {
    const cleanUrl = String(url || '').trim().replace(/\/+$/, '');
    this.baseUrl = cleanUrl || 'http://127.0.0.1:8000';
  }

  health() {
    return this.http.get<any>(`${this.baseUrl}/`);
  }

  detectSurfaces(
    image: File,
    targetClass = 'floor',
    conf = 0.25,
    imgsz = 640
  ) {
    const formData = new FormData();

    formData.append('image', image);
    formData.append('target_class', String(targetClass));
    formData.append('conf', String(conf));
    formData.append('imgsz', String(imgsz));

    return this.http.post<any>(`${this.baseUrl}/detect-surfaces`, formData);
  }
prepareRoom(
  image: File,
  conf = 0.15,
  imgsz = 640,
  targetClasses = 'floor,wall,ceiling'
) {
  const formData = new FormData();

  formData.append('image', image, image.name);
  formData.append('target_classes', targetClasses);
  formData.append('conf', String(conf));
  formData.append('imgsz', String(imgsz));
  formData.append('use_inference', 'true');

  return this.http.post<any>(
    `${this.baseUrl}/v1/rooms/analyze`,
    formData
  );
}

loadTemplateRoom(templateId: number | string, forceRefresh = false): Observable<any> {
  const key = String(templateId);

  if (!forceRefresh) {
    const cached = this.templateRoomCache.get(key);
    if (cached) {
      return cached;
    }
  }

  const request$ = this.http.get<any>(
    `${this.backendUrl}/visualizer/template-room/${encodeURIComponent(key)}`
  ).pipe(
    shareReplay({ bufferSize: 1, refCount: false })
  );

  this.templateRoomCache.set(key, request$);
  return request$;
}

clearTemplateRoomCache(templateId?: number | string): void {
  if (templateId === undefined || templateId === null) {
    this.templateRoomCache.clear();
    return;
  }

  this.templateRoomCache.delete(String(templateId));
}

  renderTemplateFloor(payload: Record<string, unknown>) {
  return this.http.post<GenerateResponse>(
    `${this.backendUrl}/visualizer/template-room/render-floor`,
    payload
  );
}

sendQuoteRequestEmail(payload: QuoteRequestPayload) {
  return this.http.post<QuoteRequestResponse>(
    `${this.backendUrl}/api/quote-request/email`,
    payload
  );
}

createQuoteWhatsappLink(payload: QuoteRequestPayload) {
  return this.http.post<QuoteRequestResponse>(
    `${this.backendUrl}/api/quote-request/whatsapp`,
    payload
  );
}

generatePrepared(
  imageId: string,
  texture: File,
  settings: GenerateSettings
) {
  const formData = new FormData();

  formData.append('image_id', imageId);
  formData.append('texture', texture);
  formData.append('target_class', String(settings.targetClass));
  formData.append('conf', String(settings.conf));
  formData.append('imgsz', String(settings.imgsz));
  formData.append('opacity', String(settings.opacity));
  formData.append('rotation_angle', String(settings.rotationAngle));
  formData.append('texture_scale', String(settings.textureScale));
  formData.append('brightness', String(settings.brightness));
  formData.append('contrast', String(settings.contrast));
  formData.append('perspective_points', settings.perspectivePoints ? JSON.stringify(settings.perspectivePoints) : '');
  formData.append('min_area_ratio', String(settings.minAreaRatio));
  formData.append('keep_largest', String(settings.keepLargest));
  formData.append('floor_top_limit_ratio', String(settings.floorTopLimitRatio));
  formData.append('wall_bottom_limit_ratio', String(settings.wallBottomLimitRatio));
  formData.append('auto_estimate_room', String(settings.autoEstimateRoom));
  formData.append('room_width_m', String(settings.roomWidthM));
  formData.append('room_depth_m', String(settings.roomDepthM));
  formData.append('plank_width_m', String(settings.plankWidthM));
  formData.append('plank_length_m', String(settings.plankLengthM));
  formData.append('pixels_per_meter', String(settings.pixelsPerMeter));
  formData.append('use_real_scale', String(settings.useRealScale));
  formData.append('pattern', String(settings.pattern));
  formData.append('grout_width_m', String(settings.groutWidthM));
  formData.append('grout_enabled', String(settings.groutEnabled));
  formData.append('grout_size_mm', String(settings.groutSizeMm || (settings.groutWidthM || 0) * 1000 || 0));
  formData.append('grout_color', String(settings.groutColor || '#CD9A6C'));

  return this.http.post<GenerateResponse>(`${this.baseUrl}/generate-prepared`, formData);
}

  prepareSurface(image: File, settings: GenerateSettings) {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('target_class', String(settings.targetClass));
    formData.append('conf', String(settings.conf));
    formData.append('imgsz', String(settings.imgsz));
    formData.append('min_area_ratio', String(settings.minAreaRatio));
    formData.append('keep_largest', String(settings.keepLargest));
    formData.append('floor_top_limit_ratio', String(settings.floorTopLimitRatio));
    formData.append('wall_bottom_limit_ratio', String(settings.wallBottomLimitRatio));
    formData.append('auto_estimate_room', String(settings.autoEstimateRoom));
    formData.append('room_width_m', String(settings.roomWidthM));
    formData.append('room_depth_m', String(settings.roomDepthM));
    return this.http.post<any>(`${this.baseUrl}/prepare-surface`, formData);
  }

  generate(image: File, texture: File, settings: GenerateSettings) {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('texture', texture);
    formData.append('target_class', String(settings.targetClass));
    formData.append('conf', String(settings.conf));
    formData.append('imgsz', String(settings.imgsz));
    formData.append('opacity', String(settings.opacity));
    formData.append('rotation_angle', String(settings.rotationAngle));
    formData.append('texture_scale', String(settings.textureScale));
    formData.append('texture_offset_x', String(settings.textureOffsetX || 0));
    formData.append('texture_offset_y', String(settings.textureOffsetY || 0));
    formData.append('brightness', String(settings.brightness));
    formData.append('contrast', String(settings.contrast));
    formData.append('perspective_points', settings.perspectivePoints ? JSON.stringify(settings.perspectivePoints) : '');
    formData.append('min_area_ratio', String(settings.minAreaRatio));
    formData.append('keep_largest', String(settings.keepLargest));
    formData.append('floor_top_limit_ratio', String(settings.floorTopLimitRatio));
    formData.append('wall_bottom_limit_ratio', String(settings.wallBottomLimitRatio));
    formData.append('auto_estimate_room', String(settings.autoEstimateRoom));
    formData.append('room_width_m', String(settings.roomWidthM));
    formData.append('room_depth_m', String(settings.roomDepthM));
    formData.append('plank_width_m', String(settings.plankWidthM));
    formData.append('plank_length_m', String(settings.plankLengthM));
    formData.append('pixels_per_meter', String(settings.pixelsPerMeter));
    formData.append('use_real_scale', String(settings.useRealScale));
    formData.append('pattern', String(settings.pattern || 'grid'));
    formData.append('grout_width_m', String(settings.groutWidthM || 0));
    formData.append('grout_enabled', String(settings.groutEnabled));
    formData.append('grout_size_mm', String(settings.groutSizeMm || (settings.groutWidthM || 0) * 1000 || 0));
    formData.append('grout_color', String(settings.groutColor || '#CD9A6C'));
    return this.http.post<any>(`${this.baseUrl}/generate`, formData);
  }

  texturePrompt(pattern = 'herringbone') {
    return this.http.get<any>(`${this.baseUrl}/texture/prompt`, {
      params: { pattern }
    });
  }


  resolveBackendUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }

    const cleanUrl = String(url).trim();

    if (!cleanUrl) {
      return null;
    }

    if (
      cleanUrl.startsWith('http://') ||
      cleanUrl.startsWith('https://') ||
      cleanUrl.startsWith('blob:') ||
      cleanUrl.startsWith('data:')
    ) {
      const backendAssetUrl = this.rewriteBackendAssetUrl(cleanUrl);

      if (backendAssetUrl) {
        return backendAssetUrl;
      }

      return cleanUrl;
    }

    // Angular local assets jangan diarahkan ke FastAPI.
    if (
      cleanUrl.startsWith('assets/') ||
      cleanUrl.startsWith('/assets/')
    ) {
      return cleanUrl.startsWith('/')
        ? cleanUrl.slice(1)
        : cleanUrl;
    }

    // File asset dari Node backend, termasuk template room.
    if (cleanUrl.startsWith('/uploads/')) {
      return `${this.backendUrl}${cleanUrl}`;
    }

    // File hasil Python/FastAPI.
    if (
      cleanUrl.startsWith('/upload/') ||
      cleanUrl.startsWith('/mask/') ||
      cleanUrl.startsWith('/slice/') ||
      cleanUrl.startsWith('/result/') ||
      cleanUrl.startsWith('/texture/')
    ) {
      return `${this.baseUrl}${cleanUrl}`;
    }

    return cleanUrl;
  }
  fetchAssetAsFile(url: string, filename = 'asset.jpg'): Observable<File> {
    const finalUrl = this.resolveAssetUrl(url);
    const cacheKey = `${finalUrl}::${filename}`;
    const cached = this.assetFileCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const request$ = finalUrl.startsWith('blob:')
      ? from(
          fetch(finalUrl)
            .then((response) => response.blob())
            .then((blob) => new File([blob], filename, { type: blob.type || 'image/jpeg' }))
        )
      : this.http.get(finalUrl, { responseType: 'blob' }).pipe(
          map((blob) => new File([blob], filename, { type: blob.type || 'image/jpeg' }))
        );

    const shared$ = request$.pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.assetFileCache.set(cacheKey, shared$);
    return shared$;
  }

  preloadAssetFiles(urls: Array<string | null | undefined>): void {
    for (const url of urls) {
      if (!url) {
        continue;
      }

      const cleanUrl = String(url).trim();

      if (!cleanUrl || cleanUrl.startsWith('data:')) {
        continue;
      }

      const filename = cleanUrl.split('/').pop()?.split('?')[0] || 'asset.jpg';
      this.fetchAssetAsFile(cleanUrl, filename).subscribe({
        error: () => {
          // Preload bersifat optional. Render tetap jalan walaupun ada asset gagal preload.
        }
      });
    }
  }

  clearAssetFileCache(): void {
    this.assetFileCache.clear();
  }

  resolveAssetUrl(url: string | null | undefined): string {
    if (!url) {
      return '';
    }

    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      const backendAssetUrl = this.rewriteBackendAssetUrl(url);

      if (backendAssetUrl) {
        return backendAssetUrl;
      }

      return url;
    }

    if (url.startsWith('/uploads/')) {
      return `${this.backendUrl}${url}`;
    }

    if (url.startsWith('/')) {
      return `${this.baseUrl}${url}`;
    }

    return url;
  }

  private getBackendUrl(): string {
    const configuredUrl = String(environment.backendUrl || '').trim();

    if (configuredUrl) {
      return configuredUrl.replace(/\/+$/, '');
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin.replace(/\/+$/, '');
    }

    return '';
  }

  private rewriteBackendAssetUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url);
      const isLocalBackend =
        parsedUrl.hostname === 'localhost' ||
        parsedUrl.hostname === '127.0.0.1';

      if (isLocalBackend && parsedUrl.pathname.startsWith('/uploads/')) {
        return `${this.backendUrl}${parsedUrl.pathname}${parsedUrl.search}`;
      }
    } catch {
      return null;
    }

    return null;
  }
}
